import { KebabButton } from '@src/components/common/contextMenuStyles';
import { GeneratingNameLabel } from '@src/components/common/GeneratingNameLabel';
import { InlineRenameField } from '@src/components/common/InlineRenameField';
import { SpeakerFacePile } from '@src/components/common/SpeakerFacePile';
import { wordIndexAtTime } from '@src/services/reel/reelSectionWords';
import ChevronDownIcon from '@src/svgs/ChevronDownIcon';
import DragHandleIcon from '@src/svgs/DragHandleIcon';
import MoreVertIcon from '@src/svgs/MoreVertIcon';
import { convertTimeToMMSS } from '@src/utility/timeFormat';
import React, { useState } from 'react';
import styled, { css, keyframes } from 'styled-components';

import { RowPlayToggle, RowPlayTracker } from './RowPlayTracker';
import { SectionTranscriptFold } from './SectionTranscriptFold';

import type { ReelEditorRow } from './useReelEditor';
import type { ReelSectionWord } from '@src/services/reel/reelSectionWords';
import type { DatoSpeaker } from '@src/types.ts/story';

interface ReelSectionRowProps {
  row: ReelEditorRow;
  /** Frame from the source video at this Section's start; omitted while unknown. */
  thumbnailUrl?: string;
  /** Identified speakers heard in this Section; empty when none are known. */
  speakers?: DatoSpeaker[];
  isDragging?: boolean;
  /**
   * The playhead is in this Section (PLAT-11073). An indicator, never a
   * selection — every action on the row behaves exactly as it does unmarked.
   */
  isActive?: boolean;
  /**
   * The player is running inside this Section, so its thumbnail toggle shows
   * a pause. True on at most one row; false elsewhere keeps the others' memo
   * intact while playback ticks.
   */
  isPlayingHere?: boolean;
  /**
   * This Section just arrived via Add Clip / Replace: a brief gray flash says
   * where it was placed (the clip grid's recently-saved highlight, in this
   * panel's hover token). The panel clears it after the flash has run.
   */
  isJustAdded?: boolean;
  /** How long the arrival flash runs; the panel picks per trigger. */
  flashMs?: number;
  /** Which flash: 'added' (gray, an insert) or 'reveal' (gold, a landing). */
  flashTone?: 'added' | 'reveal';
  /**
   * The instance-vs-asset note, when this Section was trimmed/edited relative
   * to the clip it came from — shown as a tooltip on the duration badge, the
   * number that changed. Absent = the Section matches its source (or the
   * lineage is unknown) and the badge stays mute.
   */
  sourceNote?: string;
  /** The panel's shared react-tooltip instance the note anchors to. */
  tooltipId?: string;
  /**
   * How far the playhead is through this Section, 0–1. Only the active row gets
   * one: it is the only row whose bar there is anything to draw, and it keeps
   * every other row's `React.memo` short-circuit intact while the number ticks.
   */
  progress?: number;
  /**
   * What is said in this Section, word by word — for the karaoke marking on
   * the line beneath the title, and for the transcript folded open under the
   * row (PLAT-11246 rework). Only expanded rows and the one being played are
   * given them, so every other row's memo holds while playback ticks.
   */
  words?: ReelSectionWord[];
  /**
   * This Section has transcript behind it, so the disclosure chevron is
   * offered. A cheap flag the panel reads off the row's element ids — the
   * words themselves are only walked out for rows that actually open.
   */
  hasTranscript?: boolean;
  /**
   * PLAT-11246 rework — the transcript folds open under this row. Expansion
   * is deliberate: the chevron's click, or the panel auto-expanding the row
   * being played (the active-player pattern). Never a hover.
   */
  isExpanded?: boolean;
  onToggleExpanded?: (sectionId: string) => void;
  /**
   * A word in the fold was clicked: seek there — and, on a paused player,
   * start playing from it. Falls back to `onScrub` when absent.
   */
  onWordSeek?: (time: number) => void;
  /**
   * The chapter this row runs under, and whether it is the one the chapter
   * starts at — which is what makes the gap above it that chapter's start
   * rather than the end of the chapter above (PLAT-11075 DS1). Primitives, so
   * the row stays memoized through a drag.
   */
  chapterId?: string;
  isFirstInChapter?: boolean;
  /**
   * PLAT-11056 — which chapter this row sits in, by position. A Chapter drag
   * lands between chapters, so every zone reports the boundary above the
   * chapter it belongs to (DS2) — a row included.
   */
  chapterIndex?: number;
  /**
   * Clicking the row puts the playhead at this Section's start — the same
   * binding as `isActive`, in reverse. Takes the time rather than the id, so
   * the row hands over what a seek needs and nothing more.
   */
  onSelect?: (startTime: number) => void;
  /**
   * Where on the play tracker the creator put the playhead, as an absolute reel
   * time — the tracker is a scrubber, not only a readout. Only the marked row
   * draws one, so only it ever calls this.
   */
  onScrub?: (time: number) => void;
  /**
   * The thumbnail's play/pause was clicked: play from this Section (or pause /
   * resume in place when it is the one being played) — so starting playback
   * doesn't mean leaving the row being read for the canvas transport.
   */
  onPlayToggle?: (row: ReelEditorRow) => void;
  /** Commits a typed name; the empty string clears it (PLAT-11054). */
  onRename?: (sectionId: string, name: string) => void;
  /**
   * The menu's Edit Name asked this row to open its title field (PLAT-11397).
   * Held by the panel rather than the row, for the reason the menu target is:
   * one menu serves the whole list, so the ask arrives with an id and the row it
   * names is the one that answers it.
   */
  isRenameRequested?: boolean;
  /** Called the moment that field closes, committed or not, so the ask is spent. */
  onRenameRequestHandled?: () => void;
  /**
   * PLAT-11059 — a name is being generated for this Section. Shown in the name
   * area, which is where the result lands; the row grows no control for it
   * (DE18), the action lives on the right-click menu.
   */
  isGeneratingName?: boolean;
  /**
   * PLAT-11059 DS4 — why the last generation failed. Rendered under the name,
   * which keeps what it had: a row whose name blanks out on a flaky request is
   * worse than one that just did not improve.
   */
  nameError?: string;
  /**
   * PLAT-11060 — where a right-click landed on this row, for the panel's single
   * context menu (DS2). The row reports the point and keeps no state of its own,
   * so its memo is untouched.
   */
  onContextMenu?: (sectionId: string, x: number, y: number) => void;
  /**
   * The menu is open on this row (DS4). A `position: fixed` menu over a
   * scrolling list is otherwise ambiguous about which row it belongs to — so the
   * row holds its hover fill, the trick `InsertDivider` uses to keep its `+`
   * revealed while its own menu is up.
   */
  hasOpenMenu?: boolean;
  onDragStart?: (e: React.DragEvent, index: number) => void;
  onDragOver?: (
    e: React.DragEvent,
    index: number,
    rowId: string,
    chapterId?: string,
    isFirstInChapter?: boolean,
    chapterIndex?: number,
  ) => void;
  onDrop?: (e: React.DragEvent) => void;
  onDragEnd?: () => void;
}

/**
 * How far short of the Section's end a scrub is allowed to land.
 *
 * At exactly `endTime` the playhead belongs to the *next* Section — that is how
 * `sectionIdAtTime` reads a boundary — which would take the marking, and the
 * tracker with it, out from under a drag still in progress. Under a frame at
 * any rate up to 60fps, so the frame on the canvas is the Section's last either
 * way.
 */
export const SCRUB_END_MARGIN_SECONDS = 0.01;

/**
 * A scrub target moved back to the start of the word it lands in.
 *
 * The tracker is read against the words folded open under it — the pointer marks
 * one the whole way along — so a click on it is aimed at a word, not at an
 * instant. Landing on the raw pointer time starts playback with half of that
 * word already said, which reads as the seek having missed.
 *
 * Falls through to the exact time when the passage has no words to snap to (a
 * Section whose words are all cut) or the pointer is past the last one.
 */
function snapToWordStart(
  time: number,
  words: ReelSectionWord[] | undefined,
): number {
  if (!words?.length) return time;
  // Reel time on both sides: a word's `startTime` is the reel's clock, the same
  // one the fold marks against and the same one a word click seeks to.
  const word = words[wordIndexAtTime(words, time)];
  return word ? word.startTime : time;
}

/**
 * How long a just-added Section's arrival flash runs. The panel clears the
 * flag shortly after, so the two read this one number.
 */
export const SECTION_ADDED_FLASH_MS = 2000;

/**
 * How long the arrival flash runs when the creator LANDS here from the
 * transcript's way-back control. Longer than an add's flash: an add happens
 * where the creator is already looking, a tab flip does not.
 */
export const SECTION_REVEAL_FLASH_MS = 4000;

/**
 * PLAT-11051 — one Section: a frame from where it starts, its position in the
 * reel, its name, who speaks it and how long it runs.
 *
 * The grip marks the row as draggable; the drag itself is wired up in
 * `useReelEditorDrag`.
 *
 * Memoized: a drag re-renders the panel on every pointer move, and only the
 * one or two rows whose drag state actually changed need to repaint with it.
 */
export const ReelSectionRow: React.FC<ReelSectionRowProps> = React.memo(
  ({
    row,
    thumbnailUrl,
    speakers = [],
    isDragging = false,
    isActive = false,
    isPlayingHere = false,
    isJustAdded = false,
    flashMs = SECTION_ADDED_FLASH_MS,
    flashTone = 'added',
    sourceNote,
    tooltipId,
    progress,
    words,
    hasTranscript = false,
    isExpanded = false,
    onToggleExpanded,
    onWordSeek,
    chapterId,
    isFirstInChapter,
    chapterIndex,
    onSelect,
    onScrub,
    onPlayToggle,
    onRename,
    isRenameRequested,
    onRenameRequestHandled,
    isGeneratingName = false,
    nameError,
    onContextMenu,
    hasOpenMenu = false,
    onDragStart,
    onDragOver,
    onDrop,
    onDragEnd,
  }) => {
    // The line beneath the title — the Section's opening sentence, part of the
    // row's resting layout (PLAT-11246). Every row carries one, the unnamed
    // included: an unnamed Section's fallback title opens with the same words,
    // and that repetition is accepted so the rows read as one uniform shape
    // instead of the line marking which Sections happen to have names.
    // (PLAT-11188 DS13 suppressed the repeated ones while the full passage
    // could fold open underneath; the fold is gone.)
    const firstLine = row.firstLine || null;

    // The word being spoken, marked on that line while the playhead is in
    // this Section (PLAT-11246). -1 marks nothing — the silence before the
    // first word, or any row the playhead is not in.
    const activeWordIndex =
      words?.length && progress !== undefined
        ? wordIndexAtTime(words, row.startTime + progress * row.duration)
        : -1;

    // PLAT-11072 — the Section is set aside: still listed, still named, still
    // draggable and renameable, but not in the reel. The row says so by dimming
    // and by striking its length through (DS7); what it does *not* get is a
    // control of its own (DS6), so everything below is subtraction — the play
    // toggle and the click-to-seek have nothing on the timeline to act on.
    const isOff = !row.enabled;

    // A draggable ancestor wins over text selection inside an input, so the row
    // stops being draggable while its name is open for editing.
    const [isRenaming, setIsRenaming] = useState(false);

    // A tracker drag is in flight, reported up by the tracker itself: a
    // draggable ancestor wins over a pointer drag inside it, so the row has to
    // stand its own drag down for the length of a scrub.
    const [isScrubbing, setIsScrubbing] = useState(false);

    // Where the pointer sits on the tracker, in this Section's own clock, also
    // reported up by the tracker. The bar's own fill is the tracker's business;
    // this is here because the fold is marked by a different rule.
    const [pointerTime, setPointerTime] = useState<number | null>(null);

    // What the timecodes above the words name (PLAT-11188 DS6): the pointer wins
    // as soon as it is *on* the tracker, drag or no drag — where the bar's fill
    // only follows a pointer that is dragging, since a fill that moved on a
    // hover would read as having seeked there.
    const markedTime =
      row.startTime + (pointerTime ?? (progress ?? 0) * row.duration);

    // The fold marks the two apart. The playhead's word keeps its highlight
    // wherever the pointer goes — a marking that slid away under a hover left
    // nothing on the page saying where playback actually is — and the pointer's
    // word gets the lighter one.
    const playheadTime = row.startTime + (progress ?? 0) * row.duration;
    const previewTime =
      pointerTime === null ? undefined : row.startTime + pointerTime;

    // The sub-line under the name is the two clocks rather than the opening
    // line — read here as well as rendered below, because it is also what
    // decides whether the tracker floats a readout: the clocks say the same
    // two times, so the box would only repeat them over the title it covers.
    const showsSubLineTimes =
      !nameError && !isGeneratingName && isExpanded && !!words?.length;

    return (
      <Row
        data-testid={`reel-section-row-${row.index}`}
        data-section-id={row.id}
        // The row carries `draggable`, not the grip: HTML5 DnD drags whichever
        // element has the attribute, so a draggable grip would drag only itself
        // and leave the row behind. The grip is the affordance.
        //
        // A draggable ancestor also wins over a pointer drag inside it, so the
        // row stands its own drag down while the tracker is being scrubbed —
        // otherwise sliding the playhead along the bar would lift the row out of
        // the list instead. `pointerdown` is a discrete event, so this is
        // committed before a `dragstart` could fire.
        draggable={!!onDragStart && !isRenaming && !isScrubbing}
        // The gold marking lives on the handle column, and the grip inside it is
        // `aria-hidden` — so this is the only thing that says "the playhead is
        // here" to anyone not looking at the colour.
        aria-current={isActive || undefined}
        data-just-added={isJustAdded || undefined}
        $flashMs={flashMs}
        $flashTone={flashTone}
        data-off={isOff || undefined}
        $isDragging={isDragging}
        $isOff={isOff}
        $isSelectable={!!onSelect && !isOff}
        $hasOpenMenu={hasOpenMenu}
        $justAdded={isJustAdded}
        onContextMenu={e => {
          if (!onContextMenu) return;
          // Text being edited keeps the browser's own menu — cut, copy, paste,
          // spellcheck — because that is the only way to paste a name in (DS6).
          if ((e.target as HTMLElement).closest('input, textarea')) return;
          e.preventDefault();
          // The context-menu key and Shift+F10 fire `contextmenu` natively with
          // no useful pointer position, so the menu opens on the row itself
          // rather than in the corner of the window.
          const hasPointer = e.clientX !== 0 || e.clientY !== 0;
          const rect = e.currentTarget.getBoundingClientRect();
          onContextMenu(
            row.id,
            hasPointer ? e.clientX : rect.left,
            hasPointer ? e.clientY : rect.bottom,
          );
        }}
        onClick={e => {
          // The row's own controls keep their clicks: the rename field opens
          // an input, the pencil opens it too, and a face pile avatar is its
          // own target. Only the row itself seeks.
          if ((e.target as HTMLElement).closest('button, input')) return;
          // An off Section has no position on the reel, so there is nowhere for
          // a click on it to put the playhead (PLAT-11072 DS1).
          if (isOff) return;
          onSelect?.(row.startTime);
        }}
        onDragStart={e => onDragStart?.(e, row.index)}
        onDragOver={e =>
          onDragOver?.(
            e,
            row.index,
            row.id,
            chapterId,
            isFirstInChapter,
            chapterIndex,
          )
        }
        onDrop={onDrop}
        onDragEnd={onDragEnd}
      >
        <Header data-testid={`reel-section-header-${row.index}`}>
          <HandleColumn>
            <Position
              data-testid={`reel-section-position-${row.index}`}
              $isActive={isActive}
            >
              {row.index + 1}
            </Position>
            <Handle
              aria-hidden="true"
              data-testid={`reel-section-handle-${row.index}`}
              $isActive={isActive}
            >
              <DragHandleIcon />
            </Handle>
          </HandleColumn>
          <Body>
            <Thumbnail>
              {/* Not `loading="lazy"`: the clip preview hides inactive tabs with
              `display: none`, and a lazy image in a hidden subtree never
                becomes near-viewport, so it would not load until the tab was
                opened. `usePreloadThumbnails` has warmed these already. */}
              {thumbnailUrl ? (
                <ThumbnailImage src={thumbnailUrl} alt="" decoding="async" />
              ) : (
                <ThumbnailPlaceholder />
              )}
              {/* The one mark that says "off", in the corner of the frame rather
                  than on the row: the row's width is already spoken for (DS6). */}
              {isOff && <OffBadge data-off-badge>Off</OffBadge>}
              <ThumbnailDuration
                data-thumb-duration
                data-tooltip-id={sourceNote ? tooltipId : undefined}
                data-tooltip-content={sourceNote}
                aria-label={sourceNote}
                $hidden={!isOff && !!onPlayToggle && isPlayingHere}
                // Struck through rather than hidden: the passage still has a
                // length, it just is not part of what the reel runs to (DS7).
                $struck={isOff}
              >
                {convertTimeToMMSS(row.duration)}
              </ThumbnailDuration>
              {onPlayToggle && !isOff && (
                <RowPlayToggle
                  isPlaying={isPlayingHere}
                  playLabel="Play from This Clip"
                  testId={`reel-section-play-${row.index}`}
                  onToggle={() => onPlayToggle(row)}
                />
              )}
            </Thumbnail>
            <Details>
              <InlineRenameField
                // Remounted when the menu asks for the field, which is what
                // opens it: `autoEdit` is an initial state, so the flag alone
                // would do nothing on a field that is already resting here.
                key={isRenameRequested ? 'editing' : 'resting'}
                autoEdit={isRenameRequested}
                value={row.storedName ?? ''}
                // The fallback the row already shows. Editing opens on it, and
                // accepting it unchanged stores nothing — so it stays a fallback
                // rather than becoming a hand-typed name (DS6).
                placeholder={row.openingWords}
                ariaLabel={`Rename clip ${row.index + 1}`}
                testId={`reel-section-name-${row.index}`}
                // The transcript panel is this hover's answer; the browser's
                // own box would float on top of it repeating the title.
                nativeTooltip={false}
                // No pencil or wash on hover here (PLAT-11397 — see Row): the
                // kebab's Edit Name is the way in, keyboard focus still shows it.
                hoverReveal={false}
                onEditingChange={editing => {
                  setIsRenaming(editing);
                  // Committed or cancelled, so the request is spent — else the
                  // key would hold the field open and reopen it on every
                  // render.
                  if (!editing) onRenameRequestHandled?.();
                }}
                onCommit={name => onRename?.(row.id, name)}
              />
              {/* One line under the name, and only ever one: a failure is the
                more urgent of the two, and the generating note replaces the
                opening line rather than pushing the row taller mid-generation. */}
              {nameError ? (
                <NameError role="status" title={nameError}>
                  {nameError}
                </NameError>
              ) : isGeneratingName ? (
                <GeneratingName role="status">
                  <GeneratingNameLabel />
                </GeneratingName>
              ) : showsSubLineTimes ? (
                // The fold below says the passage in full — repeating its
                // first line up here said everything twice. The slot keeps
                // its height and answers with the two clocks instead: where
                // the playhead sits while this Section plays, where the
                // Section itself sits when it was opened by hand.
                <SubLineTimes
                  data-testid={`reel-section-subline-times-${row.index}`}
                >
                  <SubLineTimeLabel>Reel</SubLineTimeLabel>{' '}
                  {convertTimeToMMSS(
                    progress === undefined ? row.startTime : markedTime,
                  )}
                  <SubLineDot>·</SubLineDot>
                  <SubLineTimeLabel>Clip</SubLineTimeLabel>{' '}
                  {convertTimeToMMSS(
                    progress === undefined ? 0 : markedTime - row.startTime,
                  )}
                </SubLineTimes>
              ) : (
                // No `title`: the fold below says the whole passage on
                // demand, and nothing on this surface may pop up on a mere
                // hover (PLAT-11246 rework).
                firstLine && (
                  <FirstLine>
                    {/* While this Section plays, the line is its words so the
                        one being spoken can be marked in place. The line's
                        nowrap + ellipsis stay on the container, so the swap
                        and the sweep both leave every glyph where it was. */}
                    {words?.length && progress !== undefined
                      ? words.map((word, index) => (
                          <React.Fragment key={index}>
                            {index > 0 && ' '}
                            <SubLineWord
                              $isActive={index === activeWordIndex}
                              data-active={
                                index === activeWordIndex || undefined
                              }
                            >
                              {word.value}
                            </SubLineWord>
                          </React.Fragment>
                        ))
                      : firstLine}
                  </FirstLine>
                )
              )}
            </Details>
            {/* Only on the Section being played: on every row it would be a
              scrubber the row does not have, and at 0% on all but one it would
              read as a rule between them. */}
            {progress !== undefined && (
              <RowPlayTracker
                duration={row.duration}
                progress={progress}
                // No `words`: what is said here is folded open under the row
                // since PLAT-11188, so the readout is back to its two timecodes
                // and covers much less of the row it belongs to. The offset is
                // still passed — it is also where the readout's Reel time is
                // counted from.
                wordsOffset={row.startTime}
                outerLabel="Reel"
                // Nothing to float while the row's own sub-line is already
                // saying both clocks.
                showReadout={!showsSubLineTimes}
                ariaLabel={`Scrub clip ${row.index + 1}`}
                testId={`reel-section-scrub-${row.index}`}
                readoutTestId={`reel-section-scrub-readout-${row.index}`}
                trackTestId={`reel-section-progress-${row.index}`}
                onScrubbingChange={setIsScrubbing}
                onPointerTimeChange={setPointerTime}
                onScrub={time =>
                  onScrub?.(
                    Math.min(
                      // Snapped to the start of the word the pointer is over,
                      // not the instant it happened to land on: the creator
                      // aimed at a word — the fold marks one under the pointer
                      // the whole way along — and a playhead dropped mid-word
                      // starts playback with half of it already said.
                      snapToWordStart(row.startTime + time, words),
                      row.endTime - SCRUB_END_MARGIN_SECONDS,
                    ),
                  )
                }
              />
            )}
          </Body>
          {(onContextMenu ||
            speakers.length > 0 ||
            (hasTranscript && onToggleExpanded)) && (
            <RightRail>
              <RailActions>
                {/* The same menu the right-click opens, surfaced: a hidden
                  gesture was the row's only route to Replace/Remove. Revealed
                  on hover like the pencil, and pinned while its menu is up. */}
                {onContextMenu && (
                  <RowMenuButton
                    type="button"
                    data-row-menu
                    aria-label={`Clip ${row.index + 1} actions`}
                    aria-haspopup="menu"
                    aria-expanded={hasOpenMenu || undefined}
                    data-testid={`reel-section-menu-button-${row.index}`}
                    onClick={e => {
                      const rect = e.currentTarget.getBoundingClientRect();
                      onContextMenu(row.id, rect.left, rect.bottom + 2);
                    }}
                  >
                    <MoreVertIcon width="14" height="14" />
                  </RowMenuButton>
                )}
                {/* PLAT-11246 rework — the explicit way into the transcript
                    below: a disclosure on the kebab's line, in the row's
                    top-right corner (the accordion's own spot), with the
                    avatar aligned beneath it. Always visible, never a hover
                    reveal — stakeholder rule: nothing opens until a button is
                    clicked. The row being played is opened by the panel the
                    same way the active player expands itself; this same
                    chevron closes it again. */}
                {hasTranscript && onToggleExpanded && (
                  <Disclosure
                    type="button"
                    aria-expanded={isExpanded}
                    aria-label={`${isExpanded ? 'Collapse' : 'Expand'} clip ${row.index + 1} transcript`}
                    data-testid={`reel-section-disclosure-${row.index}`}
                    $collapsed={!isExpanded}
                    onClick={e => {
                      // The row's own click seeks; opening the transcript is
                      // not a seek.
                      e.stopPropagation();
                      onToggleExpanded(row.id);
                    }}
                  >
                    {/* The weight the transcript tab's heading chevrons wear,
                        so the same control reads the same on both tabs. */}
                    <ChevronDownIcon strokeWidth="2.5" />
                  </Disclosure>
                )}
              </RailActions>
              {/* Nothing to show when no speaker in this passage was ever
                identified — an anonymous placeholder would be noise, not
                information. Smaller than the default: speakers are context on
                a Section, subordinate to the chapter headers above them. */}
              {speakers.length > 0 && (
                <Speakers data-testid={`reel-section-speakers-${row.index}`}>
                  <SpeakerFacePile speakers={speakers} size={18} />
                </Speakers>
              )}
            </RightRail>
          )}
        </Header>
        {/* PLAT-11188 / PLAT-11246 rework — what this Section says, folded
            open under the row: by the chevron's click, or by the panel when
            playback enters this Section. In flow on purpose — a deliberate
            expansion may move the list, a hover must not (and none does). A
            Section with no transcript behind it folds nothing rather than an
            empty box (DS1). */}
        {isExpanded && !!words?.length && (
          <Fold>
            <SectionTranscriptFold
              words={words}
              // Only where there is a playhead to mark. On a row opened by
              // the chevron there is no word being spoken, so none is marked.
              time={progress === undefined ? undefined : playheadTime}
              previewTime={progress === undefined ? undefined : previewTime}
              // Clamped short of the Section's end for the same reason a
              // scrub is: at exactly `endTime` the playhead belongs to the
              // next Section, which would hand it the fold mid-click.
              onSeek={time =>
                (onWordSeek ?? onScrub)?.(
                  Math.min(time, row.endTime - SCRUB_END_MARGIN_SECONDS),
                )
              }
              testId={`reel-section-transcript-${row.index}`}
            />
          </Fold>
        )}
      </Row>
    );
  },
);
ReelSectionRow.displayName = 'ReelSectionRow';

/* The arrival flash: the hover token, fading out over the flash window. No
   fill-mode, so the cascade (hover, open-menu fill) takes back over the moment
   it ends. */
const addedFade = keyframes`
  from {
    background-color: var(--surface-elevated);
  }
  to {
    background-color: transparent;
  }
`;

/* The landing flash: a gold TINT (never solid --accent — design system), so
   arriving from the transcript reads as "look here" rather than as another
   hover. Distinct from the add flash on purpose: an add is routine, a landing
   is the one row the creator crossed a tab to find. */
const revealFade = keyframes`
  from {
    background-color: var(--accent-tint-strong);
  }
  to {
    background-color: transparent;
  }
`;

const Row = styled.div<{
  $isDragging?: boolean;
  $isOff?: boolean;
  $isSelectable?: boolean;
  $hasOpenMenu?: boolean;
  $justAdded?: boolean;
  $flashMs?: number;
  $flashTone?: 'added' | 'reveal';
}>`
  position: relative;
  display: flex;
  flex-direction: column;
  padding: 8px 10px;
  border-radius: var(--radius-input);
  /* The opacity is transitioned too, so a Section turned off *fades* out of the
     reel rather than snapping — the change is then something the creator sees
     happen on the row they just acted on (PLAT-11072). */
  transition:
    background-color 0.15s ease,
    opacity 0.25s ease;
  /* Set aside, not gone: dim enough to read as inactive at a glance, legible
     enough to rename and drag (PLAT-11072 DS7). A drag still wins, so a row
     that is both dragging and off does not compound to invisible. */
  opacity: ${props => (props.$isDragging ? 0.4 : props.$isOff ? 0.55 : 1)};
  ${props =>
    props.$justAdded &&
    css`
      animation: ${props.$flashTone === 'reveal' ? revealFade : addedFade}
        ${props.$flashMs ?? SECTION_ADDED_FLASH_MS}ms ease-out;
    `}
  /* Clicking anywhere on it moves the playhead here, so it reads as a target
     rather than as a label with controls on it. */
  ${props => props.$isSelectable && 'cursor: pointer;'}
  /* An open menu holds the hover fill (DS4): a four-item menu is tall enough to
     cover the row it belongs to, and moving the pointer into it leaves the row.
     The kebab that opened it stays revealed for the same reason. */
  ${props =>
    props.$hasOpenMenu &&
    `background-color: var(--surface-elevated);
     [data-row-menu] {
       opacity: 1;
     }`}

  &:hover {
    background-color: var(--surface-elevated);
  }

  &:hover [data-row-menu] {
    opacity: 1;
  }

  /* No rename pencil on hover (PLAT-11397). The name column here is ~140px, and
     an 11px glyph revealed over its last word was the least legible thing on the
     row — the menu's Edit Name says it in words instead, and clicking the title
     still opens it for anyone who knows. InlineRenameField reveals the pencil
     on keyboard focus regardless, which is the one place it earns its room: the
     field is the focused element, so nothing else is competing for the eye. */

  /* The thumbnail's play/pause reveals the same way — and the duration badge
     yields to it, so the two never stack in the little frame. An off row has no
     play/pause to yield to, so its duration stays put rather than leaving an
     empty frame under the pointer. */
  ${props =>
    !props.$isOff &&
    css`
      &:hover [data-thumb-duration] {
        opacity: 0;
      }
    `}
  &:hover [data-play-toggle] {
    opacity: 1;
  }

  /* No insertion line of its own: the rule already in the gap goes green, so a
     drop is one line and not two (PLAT-11075 DS12). */
`;

/**
 * The card's one line: the handle column, the body, the right rail and the
 * disclosure. Hovering never changes its height — the fold below opens only
 * on the chevron's click or on playback entering the Section (PLAT-11246
 * rework), so a deliberate act is the only thing that moves the list.
 */
const Header = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

/**
 * PLAT-11188 — where the expanded Section's transcript sits: under the card,
 * and under the play tracker, which lands between the two. That is the right
 * reading of the bar — it is the scrub, and the words below it are what is at
 * the position it names.
 *
 * The top margin clears the tracker's 14px hit area, which hangs 8px below the
 * `Body` it is positioned against; without it the first line of text would take
 * clicks meant for the bar.
 */
const Fold = styled.div`
  margin-top: 12px;
`;

/* The rail's title line: the kebab, then the disclosure in the row's
   top-right corner — one control row, with the face pile aligned under the
   chevron on the line below.

   On the title's line, not the card's top edge: the rail is pulled 6px past the
   padding so the avatar can sit on the bottom one, and these two would ride up
   with it — reading as chrome attached to the card rather than controls for the
   name they act on. The pull is cancelled here alone, which leaves the avatar
   where it is. */
const RailActions = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  margin-top: 6px;
`;

/**
 * The transcript disclosure (PLAT-11246 rework) — the chapter rows' chevron
 * idiom on the kebab's line: down when open, right when folded, no tooltip
 * (the aria-label speaks for the glyph).
 */
const Disclosure = styled.button<{ $collapsed?: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 14px;
  height: 16px;
  padding: 0;
  border: none;
  background: none;
  color: var(--text-muted);
  cursor: pointer;
  flex-shrink: 0;
  transform: rotate(${props => (props.$collapsed ? '-90deg' : '0deg')});
  transition:
    transform var(--transition-fast),
    color var(--transition-fast);

  &:hover {
    color: var(--text-primary);
  }
`;

const HandleColumn = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
  flex-shrink: 0;
`;

/**
 * PLAT-11073 — the playhead's Section, marked on the handle column: gold
 * (`--accent`, the design system's visual-indication colour, so it cannot be
 * misread as something to click).
 *
 * Solid, not pulsing. The colour is the whole of the marking — a blink on it
 * only made the one row the creator is watching the one thing in the panel
 * that will not sit still.
 */
const playheadMarking = css`
  color: var(--accent);
`;

/**
 * The clip's position in the reel. A step up from the 10px it wore, and bold:
 * it is how a creator tells two uses of the same clip apart, and it is read
 * across from the transcript's own numbered heading — which carries the same
 * number at the same weight.
 */
const Position = styled.span<{ $isActive?: boolean }>`
  font-size: 11px;
  line-height: 13px;
  font-weight: var(--font-weight-bold);
  color: var(--text-muted);
  font-variant-numeric: tabular-nums;

  ${props => props.$isActive && playheadMarking}
`;

const Handle = styled.span<{ $isActive?: boolean }>`
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
  color: var(--text-muted);
  cursor: grab;

  &:active {
    cursor: grabbing;
  }

  ${props => props.$isActive && playheadMarking}
`;

/**
 * The thumbnail and the text as one block. It exists so the play tracker under
 * them has something to measure against: the face pile's width varies with how
 * many speakers a Section has, so a bar positioned against the card could not
 * know where to stop.
 */
const Body = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
  flex: 1;
`;

const Thumbnail = styled.div`
  position: relative;
  flex-shrink: 0;
  width: 64px;
  height: 36px;
  border-radius: var(--radius-input);
  overflow: hidden;
  background-color: var(--surface-subtle);
`;

const ThumbnailImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
`;

const ThumbnailPlaceholder = styled.div`
  width: 100%;
  height: 100%;
  background-color: var(--surface-subtle);
`;

/* Legible at rest, gone while the play/pause overlay is up: the two share a
   64×36 frame, so they take turns instead of stacking. The hover half of that
   lives on Row (the [data-thumb-duration] rule); this prop covers the overlay
   being held visible while the Section plays. */
const ThumbnailDuration = styled.span<{ $hidden: boolean; $struck?: boolean }>`
  position: absolute;
  right: 3px;
  bottom: 3px;
  padding: 0 4px;
  border-radius: 3px;
  background-color: rgba(0, 0, 0, 0.72);
  color: var(--gray-100);
  font-size: 10px;
  line-height: 14px;
  font-variant-numeric: tabular-nums;
  opacity: ${props => (props.$hidden ? 0 : 1)};
  transition: opacity 0.15s ease;
  ${props => props.$struck && 'text-decoration: line-through;'}
`;

/* PLAT-11072 — the "off" mark, opposite the duration in the same frame. Sized
   and positioned as that badge's twin so the two read as one pair. */
const OffBadge = styled.span`
  position: absolute;
  left: 3px;
  top: 3px;
  padding: 0 4px;
  border-radius: 3px;
  background-color: rgba(0, 0, 0, 0.72);
  color: var(--gray-100);
  font-size: 10px;
  line-height: 14px;
  font-weight: var(--font-weight-medium);
`;

const Details = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
  flex: 1;
`;

const FirstLine = styled.span`
  font-size: var(--text-xs);
  color: var(--text-muted);
  /* Lines up with the name, which is inset by the rename field's border +
     padding whether it is resting text or an open input. */
  padding-left: 6px;
  /* Tucks under the name: with the column's gap gone this pulls the line the
     rest of the way, past the rename field's own bottom padding — and 4px
     further (PLAT-11246), so the line sits close enough to the title that the
     pointer reads the two as one hover target for the transcript panel. */
  margin-top: -6px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

/**
 * One word of the sub-line while its Section plays. The mark is colour alone —
 * the fold's weight bump would widen the word and shift the rest of the line,
 * which is exactly what a marking that sweeps a resting row must not do
 * (PLAT-11246).
 */
const SubLineWord = styled.span<{ $isActive: boolean }>`
  color: ${props => (props.$isActive ? 'var(--accent)' : 'inherit')};
`;

/* The sub-line while the fold is open: the tracker readout's two clocks, in
   the line's own slot so the swap moves nothing. Deliberately quiet — smaller
   than the opening line it stands in for, no bold anywhere: the clocks are a
   reference while the eye is on the fold below, not something to watch. The
   digits carry the light, one shade over their muted keys. Tabular figures so
   the row does not tremble as the seconds run. */
const SubLineTimes = styled(FirstLine)`
  font-size: 11px;
  color: var(--text-secondary);
  font-variant-numeric: tabular-nums;
`;

/** The clock's name, one shade below the digits it names — never bold. */
const SubLineTimeLabel = styled.span`
  color: var(--text-muted);
`;

/* Dimmer than the times it divides, so the two read as one line. */
const SubLineDot = styled.span`
  color: var(--border);
  margin: 0 4px;
`;

/**
 * PLAT-11059 — the generation's own line, in the muted register of the opening
 * line it stands in for. Gold would read as a control and green as a result;
 * this is neither, it is a wait.
 */
/* The slot the generating label sits in — the colour and the motion are the
   label's own (PLAT-11059 DS6), this only places it under the name. */
const GeneratingName = styled(FirstLine)``;

/** The failure DS4 leaves behind, in the same slot and the danger colour. */
const NameError = styled(FirstLine)`
  color: var(--destructive);
`;

/* Spans the thumbnail's height so space-between lands the kebab on the title
   line and the avatars on the sub-line — actions above, context below.

   Overhanging it by 6px either side is what puts space between the two: the
   thumbnail is 36px, the kebab is 22px (a 14px glyph in the shared button's
   `--spacing-xs` padding) and the face pile is 18px, so stretched to the
   thumbnail exactly they would not even fit, let alone leave `space-between`
   anything to distribute. Stretch sizes the *margin* box, so the negative
   margins give the content box 48px — an 8px gap — while the Header still
   measures 36px. The overhang runs into the Row's own 8px padding rather than
   growing the row, so the rows keep their height, and the two then hug the row's
   corners instead of the thumbnail's edges, which is where a corner kebab
   belongs anyway. */
const RightRail = styled.div`
  margin-left: auto;
  align-self: stretch;
  margin-top: -6px;
  margin-bottom: -6px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: flex-end;
  flex-shrink: 0;
`;

/* The design system's kebab trigger, extended rather than re-implemented
   (`docs/design-system.md` — "Context / Action Menus"): the shared button brings
   the radius, the hover fill and the button semantics, and this adds only the
   hover-reveal the rows use (see Row) and its focus ring. The shared 8px
   `margin-left` goes with it — that is written for a kebab at the end of a flex
   row, and in this column it would take width off the name for nothing. */
const RowMenuButton = styled(KebabButton)`
  margin-left: 0;
  opacity: 0;
  transition:
    opacity 0.15s ease,
    background var(--transition-fast),
    color var(--transition-fast);

  &:focus-visible {
    opacity: 1;
    outline: 2px solid var(--primary);
    outline-offset: 1px;
  }
`;

const Speakers = styled.div`
  display: flex;
  align-items: center;
  flex-shrink: 0;
`;
